import React from 'react';
import { Link } from 'react-router-dom';
import {
  MapPin, MagnifyingGlass, Tag, Storefront, ArrowRight, MapTrifold,
  Handshake, UserCircle, Star, ShieldCheck, Heart, Clock, CurrencyDollar
} from '@phosphor-icons/react';
import { FeaturedCarousel } from '../components/FeaturedCarousel';
import Marquee from 'react-fast-marquee';
import QRCode from 'react-qr-code';

export default function LandingPage() {
  return (
    <div data-testid="landing-page" className="overflow-hidden">

      {/* ═══════════ HERO ═══════════ */}
      <section className="landing-hero px-4 sm:px-6 py-16 md:py-0">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center relative z-10">
          <div className="fade-in-up">
            <div className="inline-flex items-center gap-2 bg-[#4CB944] text-white border-2 border-[#004E89] rounded-full px-4 py-1.5 mb-6 shadow-[2px_2px_0px_#004E89]" data-testid="free-for-shoppers-badge">
              <ShieldCheck size={16} weight="bold" />
              <span className="text-xs font-bold uppercase tracking-wider">Always FREE for Shoppers</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#004E89] leading-[1.05] mb-6" style={{ fontFamily: 'Outfit' }}>
              Your Next<br />
              Treasure is<br />
              <span className="text-[#FF6B35]">Around the Corner</span>
            </h1>
            <p className="text-base sm:text-lg text-[#5C7A99] mb-8 max-w-md font-medium leading-relaxed">
              Discover estate sales, garage sales, auctions, and vendor events on an interactive map. Browse for free — post starting at just $3.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/explore" data-testid="hero-explore-btn">
                <button className="bg-[#FF6B35] text-white border-2 border-[#004E89] shadow-[4px_4px_0px_#004E89] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all rounded-xl font-bold px-8 py-3.5 text-lg flex items-center gap-2">
                  <MagnifyingGlass size={22} weight="bold" />
                  Explore Sales
                </button>
              </Link>
              <Link to="/register" data-testid="hero-signup-btn">
                <button className="bg-[#FFD166] text-[#004E89] border-2 border-[#004E89] shadow-[4px_4px_0px_#004E89] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all rounded-xl font-bold px-8 py-3.5 text-lg flex items-center gap-2">
                  Post a Sale
                  <ArrowRight size={22} weight="bold" />
                </button>
              </Link>
            </div>
          </div>
          <div className="hidden md:block fade-in-up" style={{ animationDelay: '0.2s' }}>
            <div className="relative">
              <div className="bg-white border-2 border-[#004E89] rounded-2xl shadow-[8px_8px_0px_#004E89] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1578855020607-18ed6e03d7dd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxjb2xvcmZ1bCUyMG5laWdoYm9yaG9vZCUyMG1hcHxlbnwwfHx8fDE3NzQ5NTk0NTd8MA&ixlib=rb-4.1.0&q=85"
                  alt="Colorful neighborhood map"
                  className="w-full h-[380px] object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-[#FFD166] border-2 border-[#004E89] rounded-xl p-3 shadow-[4px_4px_0px_#004E89]">
                <div className="flex items-center gap-2 text-[#004E89] font-bold text-sm">
                  <MapPin size={20} weight="fill" color="#FF6B35" />
                  Sales happening near you
                </div>
              </div>
              <div className="absolute -top-3 -right-3 bg-[#4CB944] border-2 border-[#004E89] rounded-xl px-3 py-1.5 shadow-[4px_4px_0px_#004E89]">
                <span className="text-white font-bold text-xs">100% Free to Browse</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ MARQUEE ═══════════ */}
      <div className="bg-[#FFD166] border-y-2 border-[#004E89] py-3.5 overflow-hidden" data-testid="marquee-section">
        <Marquee speed={40} gradient={false}>
          {[
            'ALWAYS FREE FOR SHOPPERS',
            'LOCATE SALES INSTANTLY',
            'ESTATE SALES',
            'GARAGE SALES',
            'AUCTIONS',
            'VENDOR EVENTS',
            'POST YOUR SALE FOR $3',
            'SALESEEKERS.ORG',
          ].map((text, i) => (
            <span key={i} className="text-[#004E89] font-black text-lg mx-8 tracking-wide" style={{ fontFamily: 'Outfit' }}>
              {text} <span className="text-[#FF6B35] mx-2">&#9670;</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* ═══════════ HOW IT WORKS ═══════════ */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 bg-white border-b-2 border-[#004E89]" data-testid="features-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-flex items-center bg-[#FFD166] text-[#004E89] border-2 border-[#004E89] rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wider shadow-[2px_2px_0px_#004E89] mb-5">
              How It Works
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#004E89]" style={{ fontFamily: 'Outfit' }}>
              Find Deals in <span className="text-[#FF6B35]">3 Easy Steps</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: <MapTrifold size={36} weight="duotone" />,
                step: '01',
                title: 'Browse the Map',
                desc: 'See all sales near you on an interactive map. Search by zip code, city, or let us find your location automatically.',
                color: '#FFD166',
              },
              {
                icon: <Tag size={36} weight="duotone" />,
                step: '02',
                title: 'Filter & Discover',
                desc: 'Filter by category (estate, garage, auction, vendor events), date range, and distance to find exactly what you want.',
                color: '#4CB944',
              },
              {
                icon: <Heart size={36} weight="duotone" />,
                step: '03',
                title: 'Save & Go',
                desc: 'Save favorites, get notified on sale day with the address at 6 AM, and head out to find your next treasure.',
                color: '#FF6B35',
              },
            ].map((f, i) => (
              <div key={i} className="bg-white border-2 border-[#004E89] rounded-2xl shadow-[6px_6px_0px_#004E89] p-8 hover:-translate-y-1 transition-transform" data-testid={`step-card-${i}`}>
                <div className="flex items-center justify-between mb-5">
                  <div className="w-14 h-14 rounded-xl border-2 border-[#004E89] flex items-center justify-center text-[#004E89]" style={{ backgroundColor: f.color }}>
                    {f.icon}
                  </div>
                  <span className="text-5xl font-black text-[#004E89]/10" style={{ fontFamily: 'Outfit' }}>{f.step}</span>
                </div>
                <h3 className="text-xl font-bold text-[#004E89] mb-3" style={{ fontFamily: 'Outfit' }}>{f.title}</h3>
                <p className="text-[#5C7A99] text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>

          {/* Seller callout */}
          <div className="mt-8 bg-[#FFD166] border-2 border-[#004E89] rounded-2xl shadow-[6px_6px_0px_#004E89] p-8 flex flex-col md:flex-row items-center justify-between gap-6" data-testid="seller-callout">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl border-2 border-[#004E89] bg-[#FF6B35] flex items-center justify-center">
                <Storefront size={32} weight="duotone" className="text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#004E89]" style={{ fontFamily: 'Outfit' }}>Got a Sale to Post?</h3>
                <p className="text-[#004E89]/70 text-sm font-medium">List your estate or garage sale starting at just $3. First listing is always free!</p>
              </div>
            </div>
            <Link to="/register" data-testid="seller-cta-btn">
              <button className="bg-[#FF6B35] text-white border-2 border-[#004E89] shadow-[4px_4px_0px_#004E89] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all rounded-xl font-bold px-6 py-3 flex items-center gap-2 whitespace-nowrap">
                Start Selling <ArrowRight size={18} weight="bold" />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════ PRICING PREVIEW ═══════════ */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 bg-[#F9F9F6]" data-testid="pricing-preview-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-flex items-center bg-[#4CB944] text-white border-2 border-[#004E89] rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wider shadow-[2px_2px_0px_#004E89] mb-5">
              Simple Pricing
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#004E89]" style={{ fontFamily: 'Outfit' }}>
              Plans That <span className="text-[#FF6B35]">Fit Your Needs</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {/* Basic */}
            <div className="bg-white border-2 border-[#004E89] rounded-2xl shadow-[6px_6px_0px_#004E89] p-8" data-testid="pricing-basic">
              <h3 className="text-lg font-bold text-[#004E89] mb-1" style={{ fontFamily: 'Outfit' }}>Basic Listing</h3>
              <p className="text-sm text-[#5C7A99] mb-4">Perfect for a single sale</p>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-black text-[#004E89]">$3</span>
                <span className="text-[#5C7A99] font-medium text-sm">per listing</span>
              </div>
              <ul className="space-y-2.5 text-sm text-[#004E89] font-medium mb-6">
                <li className="flex items-center gap-2"><ShieldCheck size={16} weight="bold" className="text-[#4CB944]" /> 5 photos included</li>
                <li className="flex items-center gap-2"><ShieldCheck size={16} weight="bold" className="text-[#4CB944]" /> Map pin + full details</li>
                <li className="flex items-center gap-2"><ShieldCheck size={16} weight="bold" className="text-[#4CB944]" /> Email notifications</li>
                <li className="flex items-center gap-2"><ShieldCheck size={16} weight="bold" className="text-[#4CB944]" /> First listing FREE</li>
              </ul>
              <Link to="/register" data-testid="pricing-basic-cta">
                <button className="w-full bg-white text-[#004E89] border-2 border-[#004E89] shadow-[4px_4px_0px_#004E89] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all rounded-xl font-bold py-3">
                  Get Started
                </button>
              </Link>
            </div>

            {/* Pro - Highlighted */}
            <div className="bg-[#FFD166] border-2 border-[#004E89] rounded-2xl shadow-[6px_6px_0px_#004E89] p-8 md:-translate-y-2" data-testid="pricing-pro">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-lg font-bold text-[#004E89]" style={{ fontFamily: 'Outfit' }}>Pro Unlimited</h3>
                <span className="bg-[#FF6B35] text-white text-[10px] font-bold px-2.5 py-1 rounded-full border border-[#004E89]">POPULAR</span>
              </div>
              <p className="text-sm text-[#004E89]/70 mb-4">For serious sellers</p>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-black text-[#004E89]">$50</span>
                <span className="text-[#004E89]/70 font-medium text-sm">/month</span>
              </div>
              <ul className="space-y-2.5 text-sm text-[#004E89] font-medium mb-6">
                <li className="flex items-center gap-2"><Star size={16} weight="fill" className="text-[#FF6B35]" /> Unlimited listings</li>
                <li className="flex items-center gap-2"><Star size={16} weight="fill" className="text-[#FF6B35]" /> Unlimited photos</li>
                <li className="flex items-center gap-2"><Star size={16} weight="fill" className="text-[#FF6B35]" /> Past Sales portfolio</li>
                <li className="flex items-center gap-2"><Star size={16} weight="fill" className="text-[#FF6B35]" /> First month FREE trial</li>
              </ul>
              <Link to="/register" data-testid="pricing-pro-cta">
                <button className="w-full bg-[#FF6B35] text-white border-2 border-[#004E89] shadow-[4px_4px_0px_#004E89] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all rounded-xl font-bold py-3">
                  Start Free Trial
                </button>
              </Link>
            </div>

            {/* Vendor */}
            <div className="bg-white border-2 border-[#004E89] rounded-2xl shadow-[6px_6px_0px_#004E89] p-8" data-testid="pricing-vendor">
              <h3 className="text-lg font-bold text-[#004E89] mb-1" style={{ fontFamily: 'Outfit' }}>Vendor Event</h3>
              <p className="text-sm text-[#5C7A99] mb-4">Craft fairs, food trucks & more</p>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-black text-[#004E89]">$25</span>
                <span className="text-[#5C7A99] font-medium text-sm">flat rate</span>
              </div>
              <ul className="space-y-2.5 text-sm text-[#004E89] font-medium mb-6">
                <li className="flex items-center gap-2"><ShieldCheck size={16} weight="bold" className="text-[#4CB944]" /> Unlimited photos</li>
                <li className="flex items-center gap-2"><ShieldCheck size={16} weight="bold" className="text-[#4CB944]" /> Video uploads ($2 ea)</li>
                <li className="flex items-center gap-2"><ShieldCheck size={16} weight="bold" className="text-[#4CB944]" /> Featured placement</li>
                <li className="flex items-center gap-2"><ShieldCheck size={16} weight="bold" className="text-[#4CB944]" /> Craft fairs & carnivals</li>
              </ul>
              <Link to="/register" data-testid="pricing-vendor-cta">
                <button className="w-full bg-white text-[#004E89] border-2 border-[#004E89] shadow-[4px_4px_0px_#004E89] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all rounded-xl font-bold py-3">
                  Post an Event
                </button>
              </Link>
            </div>
          </div>
          <p className="text-center text-[#5C7A99] text-sm mt-6 font-medium">
            All plans include map listing, email notifications, and sale-day address reveal at 6 AM. <Link to="/pricing" className="text-[#FF6B35] font-bold hover:underline" data-testid="see-all-pricing">See all pricing details &rarr;</Link>
          </p>
        </div>
      </section>

      {/* ═══════════ HIRE AN AGENT ═══════════ */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 bg-white border-y-2 border-[#004E89]" data-testid="hire-preview-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <div className="bg-white border-2 border-[#004E89] rounded-2xl shadow-[8px_8px_0px_#004E89] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1770199105692-9e52ff137cad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxmcmllbmRseSUyMHJlYWwlMjBlc3RhdGUlMjBhZ2VudHxlbnwwfHx8fDE3ODMyNTQ1ODl8MA&ixlib=rb-4.1.0&q=85"
                  alt="Estate sale professional"
                  className="w-full h-[340px] object-cover"
                />
              </div>
            </div>
            <div className="order-1 md:order-2">
              <span className="inline-flex items-center bg-[#FF6B35] text-white border-2 border-[#004E89] rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wider shadow-[2px_2px_0px_#004E89] mb-5">
                <Handshake size={14} weight="bold" className="mr-1.5" /> New Feature
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#004E89] mb-4" style={{ fontFamily: 'Outfit' }}>
                Need Someone to Run <span className="text-[#FF6B35]">Your Sale?</span>
              </h2>
              <p className="text-[#5C7A99] text-base sm:text-lg mb-6 leading-relaxed">
                Connect with experienced estate sale professionals in your area, or post a request and let agents come to you. It's free to browse and post.
              </p>
              <div className="space-y-3 mb-8">
                {[
                  { icon: <Handshake size={18} weight="bold" />, text: 'Browse local estate sale agents' },
                  { icon: <UserCircle size={18} weight="bold" />, text: 'Post a "help wanted" request for free' },
                  { icon: <MapPin size={18} weight="bold" />, text: 'Connect with pros in your area' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-[#004E89] font-medium text-sm">
                    <div className="w-8 h-8 rounded-lg bg-[#FFD166] border border-[#004E89] flex items-center justify-center flex-shrink-0">
                      {item.icon}
                    </div>
                    {item.text}
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-3">
                <Link to="/hire" data-testid="hire-preview-browse-btn">
                  <button className="bg-[#FF6B35] text-white border-2 border-[#004E89] shadow-[4px_4px_0px_#004E89] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all rounded-xl font-bold px-6 py-3 flex items-center gap-2">
                    <MagnifyingGlass size={18} weight="bold" /> Browse Agents
                  </button>
                </Link>
                <Link to="/hire" data-testid="hire-preview-post-btn">
                  <button className="bg-[#FFD166] text-[#004E89] border-2 border-[#004E89] shadow-[4px_4px_0px_#004E89] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all rounded-xl font-bold px-6 py-3 flex items-center gap-2">
                    <UserCircle size={18} weight="bold" /> I Need Help
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ QR CODE SECTION ═══════════ */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-[#004E89]" data-testid="qr-code-section">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-10">
          <div className="text-center md:text-left">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4" style={{ fontFamily: 'Outfit' }}>
              Scan & Start <span className="text-[#FFD166]">Treasure Hunting</span>
            </h2>
            <p className="text-[#B8D4E8] text-base sm:text-lg max-w-md leading-relaxed mb-4">
              Point your phone camera at this QR code to visit SaleSeeker instantly. Save it, share it, or print it out!
            </p>
            <p className="text-[#FFD166] font-bold text-sm">saleseekers.org</p>
            <a href="/saleseekers-flyer.png" download="SaleSeeker-QR-Flyer.png"
              className="mt-4 inline-flex items-center gap-2 bg-[#FFD166] text-[#004E89] border-2 border-[#FFD166] rounded-xl font-bold px-5 py-2.5 text-sm hover:bg-[#FFD166]/90 transition-colors shadow-[4px_4px_0px_rgba(255,255,255,0.2)]"
              data-testid="download-flyer-btn">
              <ArrowRight size={16} weight="bold" style={{ transform: 'rotate(90deg)' }} /> Download Printable Flyer
            </a>
          </div>
          <div className="bg-white border-2 border-[#FFD166] rounded-2xl shadow-[6px_6px_0px_#FFD166] p-6 flex flex-col items-center" data-testid="qr-code-card">
            <QRCode
              value="https://saleseekers.org"
              size={180}
              bgColor="#FFFFFF"
              fgColor="#004E89"
              level="H"
              data-testid="qr-code"
            />
            <p className="mt-3 text-[#004E89] font-bold text-sm" style={{ fontFamily: 'Outfit' }}>saleseekers.org</p>
          </div>
        </div>
      </section>

      {/* ═══════════ FEATURED SALES ═══════════ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20" data-testid="featured-sales-section">
        <FeaturedCarousel />
      </section>

      {/* ═══════════ CTA ═══════════ */}
      <section className="py-20 px-4 sm:px-6 bg-[#FFD166] border-y-2 border-[#004E89]" data-testid="cta-section">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#004E89] mb-6" style={{ fontFamily: 'Outfit' }}>
            Ready to Find Your Next Treasure?
          </h2>
          <p className="text-[#004E89]/70 text-base sm:text-lg mb-8 font-medium">
            Join thousands of treasure hunters finding incredible deals in their neighborhood. Always free for shoppers.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/explore" data-testid="cta-explore-btn">
              <button className="bg-[#FF6B35] text-white border-2 border-[#004E89] shadow-[4px_4px_0px_#004E89] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all rounded-xl font-bold px-8 py-4 text-lg flex items-center gap-2">
                <MagnifyingGlass size={22} weight="bold" />
                Explore Sales Now
              </button>
            </Link>
            <Link to="/register" data-testid="cta-signup-btn">
              <button className="bg-white text-[#004E89] border-2 border-[#004E89] shadow-[4px_4px_0px_#004E89] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all rounded-xl font-bold px-8 py-4 text-lg flex items-center gap-2">
                Sign Up Free
                <ArrowRight size={22} weight="bold" />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════ FOOTER ═══════════ */}
      <footer className="bg-[#004E89] border-t-2 border-[#004E89] px-4 sm:px-6 py-12" data-testid="footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <MapPin size={28} weight="fill" color="#FFD166" />
                <span className="text-2xl font-black text-white" style={{ fontFamily: 'Outfit' }}>SaleSeeker</span>
              </div>
              <p className="text-[#B8D4E8] text-sm leading-relaxed max-w-sm">
                The easiest way to discover estate sales, garage sales, and vendor events in your area. Always free for shoppers.
              </p>
            </div>
            <div>
              <h4 className="text-white font-bold text-sm mb-4 uppercase tracking-wider">Explore</h4>
              <div className="space-y-2.5">
                <Link to="/explore" className="text-[#B8D4E8] text-sm hover:text-[#FFD166] transition-colors block">Browse Sales</Link>
                <Link to="/hire" className="text-[#B8D4E8] text-sm hover:text-[#FFD166] transition-colors block">Hire an Agent</Link>
                <Link to="/shop" className="text-[#B8D4E8] text-sm hover:text-[#FFD166] transition-colors block">Shop Merch</Link>
                <Link to="/pricing" className="text-[#B8D4E8] text-sm hover:text-[#FFD166] transition-colors block">Pricing</Link>
              </div>
            </div>
            <div>
              <h4 className="text-white font-bold text-sm mb-4 uppercase tracking-wider">Company</h4>
              <div className="space-y-2.5">
                <Link to="/contact" className="text-[#B8D4E8] text-sm hover:text-[#FFD166] transition-colors block">Contact Us</Link>
                <Link to="/referral" className="text-[#B8D4E8] text-sm hover:text-[#FFD166] transition-colors block">Refer & Earn</Link>
                <Link to="/register" className="text-[#B8D4E8] text-sm hover:text-[#FFD166] transition-colors block">Sign Up Free</Link>
              </div>
            </div>
          </div>
          <div className="border-t border-white/20 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-[#B8D4E8] text-xs">&copy; {new Date().getFullYear()} SaleSeeker. All rights reserved.</p>
            <p className="text-[#B8D4E8] text-xs font-medium">saleseekers.org</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
